import React, { useState, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import jsPDF from 'jspdf';
import { 
  Info, X, Building2, Target, Calendar, Clock, AlertTriangle, 
  Users, FileText, Settings, Bell, Activity, MapPin, 
  CheckCircle, BarChart2, Layers, Globe, Shield, 
  Database, Zap, Workflow, GitBranch, Monitor, Smartphone,
  Wifi, Lock, Eye, EyeOff, Save, RefreshCw, Download,
  Upload, Trash2, Edit, Plus, Check, Sun, Moon,
  Clock4, CalendarDays, Target as TargetIcon, Brain,
  Palette, Key, Database as DatabaseIcon, Search,
  Filter, List, Grid, PieChart, ArrowRight, ArrowLeft,
  Play, Pause, Stop, RotateCcw, GitCommit, GitPullRequest,
  GitMerge, UserCheck, Activity as ActivityIcon
} from 'lucide-react';

const ProjectsSystemInfo = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [isDarkMode, setIsDarkMode] = useState(false);
  const [isExporting, setIsExporting] = useState(false);
  const modalRef = useRef(null);

  const exportToPDF = async () => {
    setIsExporting(true);
    try {
      const pdf = new jsPDF('p', 'mm', 'a4');
      const pageWidth = pdf.internal.pageSize.getWidth();
      const pageHeight = pdf.internal.pageSize.getHeight();
      const margin = 20;
      const contentWidth = pageWidth - (2 * margin);
      
      // Title
      pdf.setFontSize(24);
      pdf.setFont('helvetica', 'bold');
      pdf.text('Projects System Information', margin, 30);
      
      // Subtitle
      pdf.setFontSize(12);
      pdf.setFont('helvetica', 'normal');
      pdf.setTextColor(100, 100, 100);
      pdf.text('Comprehensive overview of project management system features and functionality', margin, 40);
      
      let yPosition = 60;
      
      // Function to add section
      const addSection = (title, features) => {
        // Section title
        pdf.setFontSize(16);
        pdf.setFont('helvetica', 'bold');
        pdf.setTextColor(0, 0, 0);
        pdf.text(title, margin, yPosition);
        yPosition += 10;
        
        // Features
        pdf.setFontSize(10);
        pdf.setFont('helvetica', 'normal');
        features.forEach((feature, index) => {
          const lines = pdf.splitTextToSize(`• ${feature}`, contentWidth - 10);
          if (yPosition + (lines.length * 5) > pageHeight - margin) {
            pdf.addPage();
            yPosition = margin + 20;
          }
          pdf.text(lines, margin + 5, yPosition);
          yPosition += lines.length * 5 + 2;
        });
        yPosition += 10;
      };
      
      // Add all sections
      Object.values(systemFeatures).forEach(section => {
        addSection(section.title, section.features);
      });
      
      // Add technical specs
      pdf.setFontSize(16);
      pdf.setFont('helvetica', 'bold');
      pdf.text('Technical Specifications', margin, yPosition);
      yPosition += 10;
      
      Object.values(technicalSpecs).forEach(section => {
        addSection(section.title, section.specs);
      });
      
      // System status
      yPosition += 10;
      pdf.setFontSize(14);
      pdf.setFont('helvetica', 'bold');
      pdf.text('System Status', margin, yPosition);
      yPosition += 8;
      
      pdf.setFontSize(10);
      pdf.setFont('helvetica', 'normal');
      pdf.text('• System Status: Online', margin, yPosition);
      yPosition += 6;
      pdf.text('• Uptime: 99.9%', margin, yPosition);
      yPosition += 6;
      pdf.text('• Monitoring: 24/7', margin, yPosition);
      yPosition += 6;
      pdf.text('• Version: v1.8.0', margin, yPosition);
      
      // Save the PDF
      pdf.save('Projects-System-Information.pdf');
    } catch (error) {
      console.error('Error generating PDF:', error);
    } finally {
      setIsExporting(false);
    }
  };

  const systemFeatures = {
    overview: {
      title: "Overall System Description",
      features: [
        "Comprehensive project management platform for construction and development projects",
        "Multi-phase project lifecycle management with automated workflows",
        "Real-time collaboration and communication tools for project teams",
        "Advanced analytics and reporting for project performance tracking",
        "Integrated document management and version control system",
        "Mobile-responsive design for on-site project management"
      ]
    },
    core: {
      title: "Core Project Management",
      features: [
        "End-to-end project lifecycle management from conception to completion",
        "Real-time project tracking and monitoring with live updates",
        "Multi-phase project planning and execution with phase dependencies",
        "Resource allocation and team management with skill-based assignments",
        "Budget tracking and cost management with variance analysis",
        "Risk assessment and mitigation strategies with automated alerts"
      ]
    },
    phases: {
      title: "Project Phases & Milestones",
      features: [
        "Site Selection - Location analysis, feasibility studies, and site approval",
        "Design Phase - Architectural design, engineering plans, and permit applications",
        "Construction Phase - Building execution, quality control, and progress tracking",
        "Opening Phase - Final inspections, handover, and operational readiness",
        "Milestone tracking with automated notifications and approval workflows",
        "Phase dependencies and critical path analysis for project optimization"
      ]
    },
    creation: {
      title: "Project Creation Process",
      features: [
        "Step 1: Project Initiation - Basic information, objectives, and stakeholders",
        "Step 2: Site Selection - Location analysis, feasibility, and site approval",
        "Step 3: Design Planning - Architectural requirements and engineering specifications",
        "Step 4: Budget Planning - Cost estimation, resource allocation, and funding",
        "Step 5: Team Assembly - Role assignments, responsibilities, and permissions",
        "Step 6: Timeline Creation - Milestone definition and schedule development"
      ]
    },
    modules: {
      title: "Module Components",
      features: [
        "Dashboard - Real-time project overview, KPIs, and performance metrics",
        "Project List - Comprehensive project catalog with filtering and search",
        "Project Details - Detailed project information, phases, and milestones",
        "Timeline - Interactive Gantt charts and milestone tracking",
        "Documents - File management, version control, and approval workflows",
        "Team - Resource allocation, collaboration, and communication tools",
        "Analytics - Performance metrics, reporting, and trend analysis",
        "Settings - System configuration, user management, and preferences"
      ]
    },
    planning: {
      title: "Planning & Scheduling",
      features: [
        "Interactive project timeline and Gantt charts",
        "Milestone tracking and deadline management",
        "Resource planning and capacity management",
        "Dependency mapping and critical path analysis",
        "Schedule optimization and conflict resolution",
        "Baseline planning and variance tracking"
      ]
    },
    collaboration: {
      title: "Collaboration & Communication",
      features: [
        "Real-time team collaboration tools",
        "Document sharing and version control",
        "Comment and feedback system",
        "Task assignment and tracking",
        "Meeting scheduling and management",
        "Notification and alert system"
      ]
    },
    analytics: {
      title: "Analytics & Reporting",
      features: [
        "Real-time project performance dashboards",
        "Advanced data visualization with charts and graphs",
        "Custom report builder with drag-and-drop interface",
        "KPI tracking and performance metrics",
        "Trend analysis and forecasting models",
        "Export capabilities (PDF, Excel, CSV)"
      ]
    },
    integrations: {
      title: "System Integrations",
      features: [
        "ERP system integration (SAP, Oracle, etc.)",
        "CRM system connectivity",
        "Financial system integration for budget tracking",
        "HR system integration for resource management",
        "Document management system integration",
        "Third-party project management tools"
      ]
    },
    security: {
      title: "Security & Compliance",
      features: [
        "Role-based access control (RBAC)",
        "Multi-factor authentication (MFA)",
        "Data encryption at rest and in transit",
        "Audit trails and activity logging",
        "GDPR and data privacy compliance",
        "Regular security updates and patches"
      ]
    },
    performance: {
      title: "Performance & Scalability",
      features: [
        "High-performance database architecture",
        "Caching mechanisms for improved response times",
        "Load balancing and auto-scaling capabilities",
        "Real-time data synchronization",
        "Mobile-responsive design for all devices",
        "Offline capability with data synchronization"
      ]
    },
    data: {
      title: "Data Management",
      features: [
        "Centralized project data warehouse",
        "Real-time data processing and analytics",
        "Data backup and disaster recovery",
        "Data validation and quality controls",
        "Historical data archiving and retention",
        "Data import/export with validation"
      ]
    },
    notifications: {
      title: "Notification System",
      features: [
        "Real-time push notifications",
        "Email notifications with customizable templates",
        "SMS alerts for critical events",
        "In-app notification center",
        "Escalation workflows for urgent matters",
        "Notification preferences and scheduling"
      ]
    },
    mobile: {
      title: "Mobile Capabilities",
      features: [
        "Progressive Web App (PWA) support",
        "Native mobile app for iOS and Android",
        "Offline data access and synchronization",
        "Mobile-optimized project management",
        "Photo and document capture capabilities",
        "GPS tracking and location services"
      ]
    }
  };

  const technicalSpecs = {
    architecture: {
      title: "System Architecture",
      specs: [
        "React.js frontend with modern hooks and context",
        "Component-based modular architecture",
        "Responsive design with Tailwind CSS",
        "Framer Motion for smooth animations",
        "Recharts for data visualization",
        "Leaflet for interactive maps"
      ]
    },
    dataFlow: {
      title: "Data Flow & State Management",
      specs: [
        "Component-level state management with React hooks",
        "Real-time data updates and synchronization",
        "Optimistic UI updates for better user experience",
        "Error handling and fallback mechanisms",
        "Data persistence and caching strategies",
        "Form validation and error management"
      ]
    },
    components: {
      title: "Component Structure",
      specs: [
        "Layout components with responsive design",
        "Dashboard components with real-time data",
        "Form components with validation",
        "Chart components for data visualization",
        "Map components for location tracking",
        "Navigation components with active states"
      ]
    }
  };

  const FeatureSection = ({ title, features, icon: Icon }) => (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      className={`backdrop-blur-sm rounded-2xl p-6 ${
        isDarkMode 
          ? 'bg-white/10 border border-white/20' 
          : 'bg-white/70 border border-gray-200/50'
      }`}
    >
      <div className="flex items-center space-x-3 mb-4">
        <div className={`p-2 rounded-lg ${
          isDarkMode ? 'bg-white/10' : 'bg-gray-100'
        }`}>
          <Icon className={`w-5 h-5 ${isDarkMode ? 'text-white' : 'text-gray-700'}`} />
        </div>
        <h3 className={`text-lg font-semibold ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
          {title}
        </h3>
      </div>
      <ul className="space-y-2">
        {features.map((feature, index) => (
          <motion.li
            key={index}
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ delay: index * 0.1 }}
            className={`flex items-start space-x-2 ${
              isDarkMode ? 'text-white/80' : 'text-gray-700'
            }`}
          >
            <CheckCircle className="w-4 h-4 text-green-400 mt-0.5 flex-shrink-0" />
            <span className="text-sm">{feature}</span>
          </motion.li>
        ))}
      </ul>
    </motion.div>
  );

  return (
    <>
      {/* Floating Info Button */}
      <motion.button
        initial={{ scale: 0, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        whileHover={{ scale: 1.1 }}
        whileTap={{ scale: 0.9 }}
        onClick={() => setIsOpen(true)}
        className="fixed bottom-6 right-6 z-50 p-4 rounded-full shadow-lg transition-all bg-gradient-to-r from-indigo-500 to-purple-600 text-white hover:shadow-xl"
      >
        <Info className="w-6 h-6" />
      </motion.button>

      {/* System Information Modal */}
      <AnimatePresence>
        {isOpen && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4"
            onClick={() => setIsOpen(false)}
          >
            <motion.div
              initial={{ opacity: 0, scale: 0.9, y: 20 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.9, y: 20 }}
              onClick={(e) => e.stopPropagation()}
              className={`w-full max-w-6xl max-h-[90vh] overflow-y-auto rounded-2xl ${
                isDarkMode 
                  ? 'bg-gray-900 border border-white/20' 
                  : 'bg-white border border-gray-200'
              }`}
            >
              {/* Header */}
              <div className={`sticky top-0 z-10 p-6 border-b ${
                isDarkMode ? 'border-white/20' : 'border-gray-200'
              }`}>
                <div className="flex items-center justify-between">
                  <div className="flex items-center space-x-3">
                    <div className={`p-3 rounded-xl ${
                      isDarkMode ? 'bg-white/10' : 'bg-indigo-100'
                    }`}>
                      <Info className={`w-6 h-6 ${isDarkMode ? 'text-white' : 'text-indigo-600'}`} />
                    </div>
                    <div>
                      <h1 className={`text-2xl font-bold ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
                        Projects System Information
                      </h1>
                      <p className={`text-sm ${isDarkMode ? 'text-white/60' : 'text-gray-600'}`}>
                        Comprehensive overview of project management system features and functionality
                      </p>
                    </div>
                  </div>
                  <div className="flex items-center space-x-2">
                    <motion.button
                      whileHover={{ scale: 1.05 }}
                      whileTap={{ scale: 0.95 }}
                      onClick={exportToPDF}
                      disabled={isExporting}
                      className={`flex items-center space-x-2 px-3 py-2 rounded-lg transition-all ${
                        isDarkMode 
                          ? 'hover:bg-white/10 text-white' 
                          : 'hover:bg-gray-100 text-gray-600'
                      } ${isExporting ? 'opacity-50 cursor-not-allowed' : ''}`}
                    >
                      {isExporting ? (
                        <>
                          <div className="w-4 h-4 border-2 border-current border-t-transparent rounded-full animate-spin"></div>
                          <span className="text-sm">Exporting...</span>
                        </>
                      ) : (
                        <>
                          <Download className="w-4 h-4" />
                          <span className="text-sm">Export PDF</span>
                        </>
                      )}
                    </motion.button>
                    <motion.button
                      whileHover={{ scale: 1.05 }}
                      whileTap={{ scale: 0.95 }}
                      onClick={() => setIsDarkMode(!isDarkMode)}
                      className={`p-2 rounded-lg transition-all ${
                        isDarkMode 
                          ? 'hover:bg-white/10 text-white' 
                          : 'hover:bg-gray-100 text-gray-600'
                      }`}
                    >
                      {isDarkMode ? <Sun className="w-5 h-5" /> : <Moon className="w-5 h-5" />}
                    </motion.button>
                    <motion.button
                      whileHover={{ scale: 1.05 }}
                      whileTap={{ scale: 0.95 }}
                      onClick={() => setIsOpen(false)}
                      className={`p-2 rounded-lg transition-all ${
                        isDarkMode 
                          ? 'hover:bg-white/10 text-white' 
                          : 'hover:bg-gray-100 text-gray-600'
                      }`}
                    >
                      <X className="w-5 h-5" />
                    </motion.button>
                  </div>
                </div>
              </div>

              {/* Content */}
              <div className="p-6 space-y-8">
                {/* System Overview */}
                <div className="grid grid-cols-1 gap-6">
                  <FeatureSection 
                    title={systemFeatures.overview.title}
                    features={systemFeatures.overview.features}
                    icon={Building2}
                  />
                </div>

                {/* Core & Phases */}
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                  <FeatureSection 
                    title={systemFeatures.core.title}
                    features={systemFeatures.core.features}
                    icon={Target}
                  />
                  <FeatureSection 
                    title={systemFeatures.phases.title}
                    features={systemFeatures.phases.features}
                    icon={Calendar}
                  />
                </div>

                {/* Creation Process & Modules */}
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                  <FeatureSection 
                    title={systemFeatures.creation.title}
                    features={systemFeatures.creation.features}
                    icon={Plus}
                  />
                  <FeatureSection 
                    title={systemFeatures.modules.title}
                    features={systemFeatures.modules.features}
                    icon={Layers}
                  />
                </div>

                {/* Planning & Collaboration */}
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                  <FeatureSection 
                    title={systemFeatures.planning.title}
                    features={systemFeatures.planning.features}
                    icon={Calendar}
                  />
                  <FeatureSection 
                    title={systemFeatures.collaboration.title}
                    features={systemFeatures.collaboration.features}
                    icon={Users}
                  />
                </div>

                {/* Analytics & Integrations */}
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                  <FeatureSection 
                    title={systemFeatures.analytics.title}
                    features={systemFeatures.analytics.features}
                    icon={BarChart2}
                  />
                  <FeatureSection 
                    title={systemFeatures.integrations.title}
                    features={systemFeatures.integrations.features}
                    icon={Globe}
                  />
                </div>

                {/* Security & Performance */}
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                  <FeatureSection 
                    title={systemFeatures.security.title}
                    features={systemFeatures.security.features}
                    icon={Shield}
                  />
                  <FeatureSection 
                    title={systemFeatures.performance.title}
                    features={systemFeatures.performance.features}
                    icon={Zap}
                  />
                </div>

                {/* Data & Notifications */}
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                  <FeatureSection 
                    title={systemFeatures.data.title}
                    features={systemFeatures.data.features}
                    icon={DatabaseIcon}
                  />
                  <FeatureSection 
                    title={systemFeatures.notifications.title}
                    features={systemFeatures.notifications.features}
                    icon={Bell}
                  />
                </div>

                {/* Mobile Capabilities */}
                <div className="grid grid-cols-1 gap-6">
                  <FeatureSection 
                    title={systemFeatures.mobile.title}
                    features={systemFeatures.mobile.features}
                    icon={Smartphone}
                  />
                </div>

                {/* Technical Specifications */}
                <div className="space-y-6">
                  <h2 className={`text-xl font-bold ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
                    Technical Specifications
                  </h2>
                  <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                    <FeatureSection 
                      title={technicalSpecs.architecture.title}
                      features={technicalSpecs.architecture.specs}
                      icon={Monitor}
                    />
                    <FeatureSection 
                      title={technicalSpecs.dataFlow.title}
                      features={technicalSpecs.dataFlow.specs}
                      icon={GitBranch}
                    />
                    <FeatureSection 
                      title={technicalSpecs.components.title}
                      features={technicalSpecs.components.specs}
                      icon={Layers}
                    />
                  </div>
                </div>

                {/* System Status */}
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  className={`backdrop-blur-sm rounded-2xl p-6 ${
                    isDarkMode 
                      ? 'bg-white/10 border border-white/20' 
                      : 'bg-white/70 border border-gray-200/50'
                  }`}
                >
                  <div className="flex items-center space-x-3 mb-4">
                    <div className={`p-2 rounded-lg ${
                      isDarkMode ? 'bg-white/10' : 'bg-gray-100'
                    }`}>
                      <ActivityIcon className={`w-5 h-5 ${isDarkMode ? 'text-white' : 'text-gray-700'}`} />
                    </div>
                    <h3 className={`text-lg font-semibold ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
                      System Status
                    </h3>
                  </div>
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                    <div className="text-center">
                      <div className="flex items-center justify-center space-x-2 mb-2">
                        <div className="w-2 h-2 bg-green-400 rounded-full animate-pulse"></div>
                        <span className={`text-sm font-medium ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
                          Online
                        </span>
                      </div>
                      <p className={`text-xs ${isDarkMode ? 'text-white/60' : 'text-gray-600'}`}>System Status</p>
                    </div>
                    <div className="text-center">
                      <p className={`text-lg font-bold ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>99.9%</p>
                      <p className={`text-xs ${isDarkMode ? 'text-white/60' : 'text-gray-600'}`}>Uptime</p>
                    </div>
                    <div className="text-center">
                      <p className={`text-lg font-bold ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>24/7</p>
                      <p className={`text-xs ${isDarkMode ? 'text-white/60' : 'text-gray-600'}`}>Monitoring</p>
                    </div>
                    <div className="text-center">
                      <p className={`text-lg font-bold ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>v1.8.0</p>
                      <p className={`text-xs ${isDarkMode ? 'text-white/60' : 'text-gray-600'}`}>Version</p>
                    </div>
                  </div>
                </motion.div>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};

export default ProjectsSystemInfo; 